/**
 * Input DTO for the Item Movement History derivation layer.
 *
 * The generated `useItemHistoryQuery` (see `useItemHistory.ts`) maps its connection
 * into this shape, which the derivation (`deriveMovementRow`) and grid consume. Keeping
 * a dedicated DTO — rather than feeding the raw generated node in — buys two things the
 * wire type can't express: `transactionType` stays a plain `string` so an unrecognised
 * server-side type degrades gracefully instead of narrowing to the `TransactionType`
 * enum. `useItemHistory.ts` is the single mapping seam.
 */

// --- Derivation input shapes (mirror the ItemHistoryEntry GraphQL response) ---

export enum ItemHistoryChangeDirection {
	Increase = 'INCREASE',
	Decrease = 'DECREASE',
}

export enum ItemHistorySourceType {
	PurchaseOrder = 'PURCHASE_ORDER',
	CreditPurchase = 'CREDIT_PURCHASE',
	TransferOrder = 'TRANSFER_ORDER',
	InventoryLocation = 'INVENTORY_LOCATION',
	WorkOrder = 'WORK_ORDER',
	Order = 'ORDER',
	Asset = 'ASSET',
	ManualAdjustment = 'MANUAL_ADJUSTMENT',
	Provider = 'PROVIDER',
	RetailSale = 'RETAIL_SALE',
	Conversion = 'CONVERSION',
	// A product merge writes the same transaction pair a user conversion does; the API
	// distinguishes them by the absorbed product carrying a duplicate marker pointing at
	// the survivor. Both resolve their `source` to the product on the other leg.
	Merge = 'MERGE',
}

/**
 * Resolved counterparty object. Mirrors the `ItemHistorySource` union — only the
 * fields the timeline renders are modelled here.
 */
export type ItemHistorySource =
	| { __typename: 'PurchaseOrder', id: string, purchaseOrderNumber: string | null }
	| { __typename: 'ManualTransferOrder', id: string }
	| { __typename: 'SystemTransferOrder', id: string }
	| { __typename: 'InventoryLocation', id: string, name: string }
	| { __typename: 'WorkOrderReference', id: string }
	| { __typename: 'OrderReference', id: string }
	| { __typename: 'Asset', id: string }
	| { __typename: 'ManualAdjustment', id: string, reason?: string | null }
	| { __typename: 'Provider', id?: string, name: string }
	| { __typename: 'StorePart', storePartId: string, isArchived: boolean, partName: string | null }
	| { __typename: 'CreditPurchaseReference', id: string }
	| { __typename: 'RetailSaleReference', id: string }

export type ItemHistoryActor = {
	firstName?: string | null,
	lastName?: string | null,
	email?: string | null,
}

export type ItemHistoryEntry = {
	id: string,
	transactionId: number,
	dateApplied: string,
	transactionStatus: string,
	// Kept as a raw string rather than the generated `TransactionType` enum: the
	// ledger grows server-side and the frontend must degrade gracefully on a type
	// it does not recognise (see the "Unrecognized movement type" case).
	transactionType: string,
	quantityChange: number,
	quantityOrdered?: number | null,
	quantityReceived?: number | null,
	costPerItem?: number | null,
	itemStatus?: string | null,
	memo?: string | null,
	customId?: string | null,
	sourceType?: ItemHistorySourceType | null,
	sourceId?: string | null,
	source?: ItemHistorySource | null,
	modifiedByUser?: ItemHistoryActor | null,
}

export type ItemHistoryConnection = {
	entries: ItemHistoryEntry[],
	totalCount: number,
}

// --- Derived view model consumed by the grid ---

/**
 * `unchanged` is a movement the API reports a signed change of 0 for — in practice a
 * self-transfer, where the sending and receiving location are the same. It is not a
 * rental custody move: the API applies no rental special case, so rentals move owned
 * quantity like anything else.
 */
export type MovementDirection = 'in' | 'out' | 'unchanged' | 'unknown'

/**
 * Whether the movement actually landed. Only three transaction statuses reach an item's
 * history, and they differ in exactly one way that matters here — did the quantity move?
 *
 * - `completed` — it moved.
 * - `in_transit` — it moved. The units have left this location; only the receiving side
 *   is outstanding, so the delta is real and counts against on-hand. Sending side only.
 * - `cancelled` — it did NOT move. Since the API stopped reporting 0 for these, the delta
 *   is the quantity that *would* have moved, which is the useful figure (how much came
 *   back) but must never read as an applied change.
 *
 * A future running balance sums `completed` and `in_transit` and skips `cancelled`.
 */
export type MovementStatus = 'completed' | 'in_transit' | 'cancelled'

export type MovementCounterparty = {
	display: string,
	/** Internal react-router route, when the counterparty links inside this app. */
	to?: string,
	/** External URL (e.g. CostCapture purchase order). */
	href?: string,
	resolved: boolean,
	/** No reference was ever recorded by this movement type. */
	missing: boolean,
}

export type MovementRow = {
	id: string,
	date: string,
	transactionType: string,
	label: string,
	direction: MovementDirection,
	status: MovementStatus,
	/**
	 * Signed quantity delta against owned quantity; null when indeterminate. On a
	 * `cancelled` row this is the quantity that would have moved — read `status` before
	 * treating it as applied.
	 */
	delta: number | null,
	/** Unsigned magnitude, shown when direction is indeterminate. */
	quantity: number,
	/** For an `unchanged` movement, how many units moved without altering the total. */
	unchangedQuantity: number | null,
	counterparty: MovementCounterparty,
	actor: string | null,
	costPerUnit: number | null,
	memo: string | null,
	flags: MovementFlag[],
}

export type MovementFlag =
	| 'id_only'
	| 'unresolved_counterparty'
	| 'no_counterparty_recorded'
	| 'unrecognized_type'
	| 'direction_inferred_from_name'
	| 'indeterminate_direction'
	| 'approximate_date'
	| 'provisional_date'
	| 'legacy_transfer'
	| 'deprecated_type'
	| 'zero_delta'
	| 'adjustment_memo_long'
	| 'cancelled'
	| 'in_transit'
	| 'archived_counterparty'

export const FLAG_LABEL: Record<MovementFlag, string> = {
	id_only: 'No link available for this reference',
	unresolved_counterparty: 'Reference could not be resolved to a name',
	no_counterparty_recorded: 'No reference recorded by this transaction type',
	unrecognized_type: 'Unrecognized transaction type',
	direction_inferred_from_name: 'Direction inferred from the type name',
	indeterminate_direction: 'Direction could not be determined',
	approximate_date: 'Approximate date — legacy transfer, no shipped-at timestamp',
	provisional_date: 'Provisional date — nothing records when stock left, so this shows when the transfer was created. It re-dates to the completion once received.',
	legacy_transfer: 'Legacy transfer, predates transfer orders',
	deprecated_type: 'Transaction type no longer creatable',
	zero_delta: 'Owned quantity unchanged',
	adjustment_memo_long: 'Manual adjustment',
	cancelled: 'Cancelled — this quantity never moved',
	in_transit: 'In transit — these units have left, but the receiving location has not confirmed them',
	archived_counterparty: 'This item has since been archived — merging removes the absorbed item from inventory',
}
